import { useEffect, useState, useCallback, Fragment } from 'react';
import { useFormikContext } from 'formik';
import lodashDebounce from 'lodash/debounce';
import TextInput from '@adsk/alloy-react-text-input';
import { LinkButton } from '@adsk/alloy-react-button';
import { T } from '../../helpers/i18n';
import { useRfiForm } from '../RfiFormProvider';
import { CommonFormField } from '../../components';
import Loader from '../../components/Loader';
import { createSDK } from '../../sdk';
import { ISSUE_ATTRIBUTE_NAMES } from '../../constants/issue-attribute-names';

const CustomIdentifierField = () => {
  const formik = useFormikContext();
  const {
    invalidIdentifiers,
    isSubmitting,
    mandatoryFields,
    projectId,
    refs,
    setInvalidIdentifiers,
  } = useRfiForm();
  const fieldName = ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER;
  const isFieldsMandatory = mandatoryFields.includes(fieldName);

  const [isFetching, setIsFetching] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [suggestedValue, setSuggestedValue] = useState(false);

  const errorMessage = !isFetching && !isValidating && formik.errors.customIdentifier;

  const validateIdentifier = (identifier) => {
    const sdk = createSDK({ projectId });
    const trimmedIdentifier = identifier.trim();

    setIsValidating(true);

    sdk.RFIs.customIdentifier.validate(trimmedIdentifier).then((isCustomIdentifierValid) => {
      setIsValidating(false);
      if (!isCustomIdentifierValid) {
        setInvalidIdentifiers((invalidValues) => [...invalidValues, trimmedIdentifier]);
        formik.setFieldError(fieldName, T.translate('new.error.customIdentifierNotAvailable'));
      }
    });
  };
  const validateIdentifierDebounced = useCallback(lodashDebounce(validateIdentifier, 2000), []);

  const setValueImperative = (value) => {
    validateIdentifierDebounced.cancel();
    formik.setFieldValue(fieldName, value);
  };

  // Initial population of customIdentifier
  useEffect(() => {
    if (projectId) {
      const sdk = createSDK({ projectId });
      setIsFetching(true);
      sdk.RFIs.customIdentifier
        .getSuggested()
        .then(({ current, next }) => {
          setInvalidIdentifiers([current]);
          setSuggestedValue(next);
          setValueImperative(next);
        })
        .finally(() => {
          setIsFetching(false);
        });
    }
  }, [projectId]);

  // Validating customIdentifier on change
  useEffect(() => {
    const { customIdentifier } = formik.values;
    validateIdentifierDebounced.cancel();
    const shouldValidateIdentifier =
      customIdentifier !== '' &&
      customIdentifier !== suggestedValue &&
      !formik.errors.customIdentifier &&
      !invalidIdentifiers.includes(customIdentifier);

    if (shouldValidateIdentifier) {
      validateIdentifierDebounced(customIdentifier);
    }
  }, [
    formik.values.customIdentifier,
    formik.errors.customIdentifier,
    suggestedValue,
    invalidIdentifiers,
  ]);

  const handleBlur = (e) => {
    formik.handleBlur(e);
    validateIdentifierDebounced.flush();
  };

  const showSuggestedLink = formik.errors.customIdentifier && !!suggestedValue;

  return (
    <Fragment>
      <CommonFormField
        label={T.translate('new.number')}
        required={isFieldsMandatory}
        error={errorMessage}
        ref={refs[fieldName]}
      >
        <div style={{ position: 'relative' }}>
          <TextInput
            disabled={isFetching || isSubmitting}
            name={fieldName}
            onChange={formik.handleChange}
            style={{ position: 'relative' }}
            onBlur={handleBlur}
            value={formik.values.customIdentifier}
            error={!!errorMessage}
          />
          {(isFetching || isValidating) && (
            <div
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                marginTop: '2px',
              }}
            >
              <Loader size={Loader.SIZES.XSMALL} noPositioner />
            </div>
          )}
        </div>
      </CommonFormField>
      {showSuggestedLink && (
        <LinkButton
          style={{
            transform: 'translateY(-20px)',
          }}
          onClick={() => setValueImperative(suggestedValue)}
        >
          {T.translate('new.getNextCustomIdentifier')}
        </LinkButton>
      )}
    </Fragment>
  );
};

export default CustomIdentifierField;
